import { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import Image from 'components/Common/Image'
import TextLink from 'LuxKit/TextLink'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ContactContext from 'contexts/contactContext'
import Group from 'components/utils/Group'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'
import GeoContext from 'contexts/geoContext'
import getCustomerSupportAgentImage from 'components/Common/CustomerSupport/getCustomerSupportAgentImage'
import { rem } from 'polished'

const ImageWrapper = styled.div`
  width: ${rem(68)};
  height: ${rem(68)};

  ${mediaQueryUp.tablet} {
    width: ${rem(84)};
    height: ${rem(84)};
  }
`

interface Props {
  className?: string
  allowsAgentCallbackRequest?: boolean
  title?: string
  description?: string
  contactOverride?: App.GeoContact
}

function ContactUSP({ className, allowsAgentCallbackRequest, title, description, contactOverride }: Props) {
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const contact = contactOverride ?? currentContact.defaultContact
  const customerSupportImage = getCustomerSupportAgentImage('left', false, currentRegionCode)
  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)

  const handleCallbackClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="TOURS" />)
  }, [showModal])

  const defaultTitle = intl.formatMessage({
    id: 'TglnTq-1y0RsJUQ_32PA',
    defaultMessage: 'Our team are available 24 hours, 7 days',
    description: 'Contact USP title about team availability in the support area',
  })

  const defaultDescription = intl.formatMessage({
    id: 'G3Rcs22okz62dBN0Yt_N',
    defaultMessage: 'Interested in this offer? Contact our dedicated support team anytime.',
    description: 'Contact USP description inviting users to contact support',
  })

  const requestCallbackText = intl.formatMessage({
    id: 'A0tiycFXihOSupEe5KS4',
    defaultMessage: 'Request a callback',
    description: 'Button text to request a callback from the customer support team for assistance',
  })

  return (
    <Group
      className={className}
      gap={16}
      tabletGap={20}
      direction="horizontal-reverse"
      tabletDirection="horizontal"
      verticalAlign="start"
      tabletVerticalAlign="center"
      horizontalAlign="space-between"
      tabletHorizontalAlign="start"
    >
      <ImageWrapper>
        <Image
          fit="center"
          image={customerSupportImage}
          quality="best"
          width={84}
        />
      </ImageWrapper>
      <Group direction="vertical" gap={4}>
        <BodyText variant="medium" weight="bold">
          {title ?? defaultTitle}
        </BodyText>
        <BodyText variant="medium" colour="neutral-three">
          {description ?? defaultDescription}
        </BodyText>
        <Group direction="horizontal" gap={32}>
          <TextLink
            size="medium"
            weight="bold"
            href={`tel:${contact.number}`}
            startIcon={<LinePhoneIcon />}
          >
            {contact.humanReadable}
          </TextLink>
          {callbacksFeatureEnabled && allowsAgentCallbackRequest && <TextLink
            size="medium"
            weight="bold"
            onClick={handleCallbackClick}
            startIcon={<LineIncomingCallIcon />}
          >
            {requestCallbackText}
          </TextLink>}
        </Group>
      </Group>
    </Group>
  )
}

export default ContactUSP
